import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

export type DevicePlatform = 'web' | 'ios' | 'android' | 'desktop'

export interface LinkedDevice {
  deviceId: string
  label: string
  platform: DevicePlatform
  linkedAt: number
  isSelf: boolean
}

interface DeviceDb extends DBSchema {
  meta: {
    key: string
    value: string
  }
  devices: {
    key: string
    value: LinkedDevice
  }
}

const META_DEVICE_ID = 'local_device_id'
const META_DEVICE_LINK_KEY = 'device_link_key_b64'

let dbPromise: Promise<IDBPDatabase<DeviceDb>> | null = null

function getDb(): Promise<IDBPDatabase<DeviceDb>> {
  if (!dbPromise) {
    dbPromise = openDB<DeviceDb>('zion-devices', 1, {
      upgrade(db) {
        db.createObjectStore('meta')
        db.createObjectStore('devices', { keyPath: 'deviceId' })
      },
    })
  }
  return dbPromise
}

function randomDeviceId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function detectPlatform(): DevicePlatform {
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  if (window.matchMedia('(pointer: fine)').matches && !('ontouchstart' in window)) return 'desktop'
  return 'web'
}

export function defaultDeviceLabel(platform = detectPlatform()): string {
  if (platform === 'ios') return 'iPhone / iPad'
  if (platform === 'android') return 'Android'
  if (platform === 'desktop') return 'PC Browser'
  return 'Web PWA'
}

export async function getLocalDeviceId(): Promise<string> {
  const db = await getDb()
  const existing = await db.get('meta', META_DEVICE_ID)
  if (existing?.trim()) return existing.trim()
  const id = randomDeviceId()
  await db.put('meta', id, META_DEVICE_ID)
  return id
}

export async function getDeviceLinkKeyB64(): Promise<string | null> {
  const db = await getDb()
  const v = await db.get('meta', META_DEVICE_LINK_KEY)
  return v?.trim() || null
}

export async function saveDeviceLinkKeyB64(b64: string): Promise<void> {
  const db = await getDb()
  await db.put('meta', b64.trim(), META_DEVICE_LINK_KEY)
}

export async function listLinkedDevices(): Promise<LinkedDevice[]> {
  const db = await getDb()
  const all = await db.getAll('devices')
  return all.sort((a, b) => b.linkedAt - a.linkedAt)
}

export async function upsertLinkedDevice(device: LinkedDevice): Promise<void> {
  const db = await getDb()
  await db.put('devices', device)
}

export async function removeLinkedDevice(deviceId: string): Promise<void> {
  const db = await getDb()
  await db.delete('devices', deviceId)
}

export async function registerSelfDevice(label?: string): Promise<LinkedDevice> {
  const deviceId = await getLocalDeviceId()
  const self: LinkedDevice = {
    deviceId,
    label: label?.trim() || defaultDeviceLabel(),
    platform: detectPlatform(),
    linkedAt: Date.now(),
    isSelf: true,
  }
  await upsertLinkedDevice(self)
  return self
}

export async function hasDeviceLink(): Promise<boolean> {
  const key = await getDeviceLinkKeyB64()
  const devices = await listLinkedDevices()
  return Boolean(key && devices.length > 0)
}

export async function clearDeviceLinkData(): Promise<void> {
  const db = await getDb()
  await db.clear('devices')
  await db.delete('meta', META_DEVICE_LINK_KEY)
}
